import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import api from '../services/api';
import { setSyncPaymentError } from '../store/slices/uiSlice';
import type { AppDispatch } from '../store';
import { trackPageView, trackWithUserContext } from '../utils/analytics';

interface GreenPayPayload {
  status: number;
  orderId: string;
  authorization: string;
  amount: number;
  currency: string;
  result: {
    success: boolean;
    resp_code: string;
  };
  errors: any[];
  callback: string;
  _signature: string;
  last4: string;
  brand: string;
  bin: string;
  additional: {
    channelData: {
      channel: string;
      source: string;
    };
  };
}

const PaymentCallback: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { provider } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    // Track payment callback page view
    trackPageView(window.location.href, `Payment Callback - ${provider}`);

    const processCallback = async () => {
      try {
        // Get the payload from the URL path        
        const payload = location.pathname.split('/payloads/')[1];
        if (!payload) {
          // throw new Error('Missing payload');
        }

        // Extract orderId from the GreenPay payload for redirect purposes
        let orderId: string;
        let paymentId: string;
        let isSuccess: boolean;

        if (provider?.toLowerCase() === 'greenpay') {
          try {
            const decodedPayload = JSON.parse(atob(payload)) as GreenPayPayload;
            paymentId = decodedPayload.orderId; // This is actually the paymentId
            isSuccess = decodedPayload.status === 200 && decodedPayload.result.success;
            
            // Call the webhook endpoint to process the payment
            // This simulates what GreenPay should do automatically once webhooks are configured
            const webhookResponse = await api.post(`/payments/providers/${provider}/payloads`, decodedPayload);
            
            // Extract the real orderId from the webhook response
            orderId = webhookResponse.data.orderId || paymentId; // Fallback to paymentId if orderId not available
            
            // Check if payment failed and extract detailed error information
            if (!isSuccess && webhookResponse.data.paymentError) {
              const detailedError = webhookResponse.data.paymentError;
              
              // Dispatch error to Redux state management
              dispatch(setSyncPaymentError({
                error: detailedError,
                orderId,
                paymentId,
                // User context will be added by ReadyToPay when it has order details
              }));
              
              // Simple redirect with error flag
              navigate(`/ready-to-pay/${orderId}?paymentError=sync`);
              return;
            }
            
          } catch (err) {
            console.error('Failed to decode payload or process payment:', err);
            throw new Error('Invalid payload format or payment processing failed');
          }
        } else if (provider?.toLowerCase() === 'onvo') {
          try {
            // For ONVO, extract identifiers from URL query parameters
            const urlParams = new URLSearchParams(location.search);
            paymentId = urlParams.get('paymentId') || 'unknown';
            orderId = urlParams.get('orderId') || paymentId;
            const status = urlParams.get('status') || 'unknown';
            
            // Determine success based on the status parameter and URL path
            isSuccess = status === 'success';
            
            console.log('ONVO callback received:', { paymentId, orderId, status, isSuccess });
            
            // Process the payment callback with the extracted information
            const webhookData = {
              paymentId,
              orderId,
              status,
              success: isSuccess,
              provider: 'onvo'
            };
            
            const webhookResponse = await api.post(`/payments/providers/${provider}/payloads`, webhookData);
            
            // Update with response data if available
            paymentId = webhookResponse.data.paymentId || paymentId;
            orderId = webhookResponse.data.orderId || orderId;
            isSuccess = webhookResponse.data.success !== undefined ? webhookResponse.data.success : isSuccess;
            
            // Check if payment failed and extract detailed error information
            if (!isSuccess && webhookResponse.data.paymentError) {
              const detailedError = webhookResponse.data.paymentError;
              
              // Dispatch error to Redux state management
              dispatch(setSyncPaymentError({
                error: detailedError,
                orderId,
                paymentId,
              }));
              
              // Simple redirect with error flag
              navigate(`/ready-to-pay/${orderId}?paymentError=sync`);
              return;
            }
            
          } catch (err) {
            console.error('Failed to process ONVO payment callback:', err);
            throw new Error('ONVO payment processing failed');
          }
        } else {
          throw new Error(`Unsupported payment provider: ${provider}`);
        }

        // Redirect to appropriate page with paymentId for success, orderId for cancel
        if (isSuccess) {
          // Track successful payment callback processing
          trackWithUserContext(
            'payment_callback_success',
            'payment_flow',
            provider || 'unknown',
            undefined,
            {
              payment_id: paymentId,
              order_id: orderId,
              provider: provider
            }
          );
          navigate(`/payment/success?paymentId=${paymentId}&orderId=${orderId}`);
        } else {
          // Track failed payment callback processing
          trackWithUserContext(
            'payment_callback_failure',
            'payment_flow',
            provider || 'unknown',
            undefined,
            {
              payment_id: paymentId,
              order_id: orderId,
              provider: provider
            }
          );

          // Fallback error message for cases where detailed error wasn't available
          const fallbackError = {
            code: 'PAYMENT_FAILED',
            originalMessage: 'Tu pago no pudo ser procesado.',
            userMessage: 'Tu pago no pudo ser procesado. Por favor verifica tu información de pago o intenta con otra tarjeta.',
            category: 'retry' as const,
            canRetry: true,
            icon: '❌',
            severity: 'error' as const
          };
          
          dispatch(setSyncPaymentError({
            error: fallbackError,
            orderId,
            paymentId,
          }));
          
          navigate(`/ready-to-pay/${orderId}?paymentError=sync`);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to process payment callback');
        
        // Track payment callback processing error
        trackWithUserContext(
          'payment_callback_error',
          'payment_flow',
          provider || 'unknown',
          undefined,
          {
            error_message: err.message || 'Payment processing failed',
            provider: provider
          }
        );
        
        // Try to extract orderId for error context
        let fallbackOrderId: string | null = null;
        
        try {
          const payload = location.pathname.split('/payloads/')[1];
          if (payload && provider?.toLowerCase() === 'greenpay') {
            const decodedPayload = JSON.parse(atob(payload)) as GreenPayPayload;
            // Try to get orderId from the webhook response first, then from payload
            const webhookResponse = await api.post(`/payments/providers/${provider}/payloads`, decodedPayload);
            fallbackOrderId = webhookResponse.data.orderId || decodedPayload.orderId;
          }
        } catch (fallbackErr) {
          console.error('Failed to extract orderId for error redirect:', fallbackErr);
        }
        
        if (fallbackOrderId) {
          const processError = {
            code: 'PROCESSING_ERROR',
            originalMessage: err.message || 'Payment processing failed',
            userMessage: 'Ocurrió un error al procesar tu pago. Por favor intenta nuevamente.',
            category: 'retry' as const,
            canRetry: true,
            icon: '⚠️',
            severity: 'error' as const
          };
          
          dispatch(setSyncPaymentError({
            error: processError,
            orderId: fallbackOrderId,
            paymentId: 'unknown',
          }));
          
          navigate(`/ready-to-pay/${fallbackOrderId}?paymentError=sync`);
        } else {
          // Track critical error with no order context
          trackWithUserContext(
            'payment_callback_critical_error',
            'payment_flow',
            'no_order_context',
            undefined,
            {
              error_message: err.message || 'Payment processing failed',
              provider: provider,
              redirect_to: 'general_error'
            }
          );
          
          // If no orderId available, redirect to general error
          navigate(`/error?message=${encodeURIComponent(err.message || 'Payment processing failed')}`);
        }
      } finally {
        setLoading(false);
      }
    };

    processCallback();
  }, [location.pathname, navigate, provider, dispatch]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return null; // This page should never render as it always redirects
};

export default PaymentCallback; 